// storage-admin.view — Kelola Penyimpanan (Pengaturan, O/M), upgrade Apo 20 Jul:
// tab per JENIS (kuasa/absensi/beban) · 👁️ preview foto · checkbox multi-pilih → unduh/hapus
// massal · filter periode · kolom "Tertaut ke" (nomor tiket bisa diklik) · unduhan ter-rename
// "<no>-<pelanggan>.jpg". Hapus tetap HANYA orphan — bukti tertaut dilindungi (kebijakan retensi
// bukti hukum/akuntansi menunggu keputusan Apo).
import { esc } from '@domain/format';
import { toast } from '@ui/toast';
import { passExportGuard } from '@ui/export-guard';
import { openModal } from '@ui/modal';
import { logAudit } from '@core/audit';
import { kebijakan, saveSetting } from '@core/settings';
import { bacaPemeliharaan, setPemeliharaan } from '@core/maintenance';
import { currentUser } from '@core/session';
import { signedUrl } from '@core/storage';
import * as repo from './storage-admin.repo';
import * as finRepo from '@modules/finance/finance.repo';

const BUCKETS: { bucket: string; label: string }[] = [
  { bucket: 'unit', label: '📷 Foto Unit' },
  { bucket: 'kuasa', label: '📝 Surat Kuasa' },
  { bucket: 'absensi', label: '🕒 Selfie Absensi' },
  { bucket: 'bukti', label: '🧾 Bukti Beban' },
  { bucket: 'dokumen', label: '📄 Dokumen Terkirim' },
];
let saTab = 'kuasa', saDari = '', saSampai = '';

const fmtBytes = (n: number): string => n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(2)} MB`;

export async function renderStoragePanel(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="scope-note">Memuat penyimpanan…</div>`;
  const def = BUCKETS.find((b) => b.bucket === saTab) ?? BUCKETS[0]!;
  let ov: repo.BucketOverview; let refs: Map<string, repo.RefInfo>;
  let totals: { bucket: string; n: number; size: number }[] = [];
  // Auto-hapus selfie tua (hari dari Pengaturan; 0=mati) — dijalankan saat panel dibuka (O/M),
  // best-effort: kegagalan tak menghalangi kelola penyimpanan.
  let selfieDibersihkan = 0;
  const retensi = kebijakan().selfieRetensiHari;
  if (saTab === 'absensi' && retensi > 0) {
    try {
      selfieDibersihkan = await repo.cleanupSelfies(retensi);
      if (selfieDibersihkan) void logAudit({ action: 'STORAGE_AUTOCLEAN', module: 'backup', refId: `absensi · ${selfieDibersihkan} selfie > ${retensi} hari` });
    } catch { /* lapor lewat baris info saja */ }
  }
  try {
    const all = await Promise.all(BUCKETS.map((b) => repo.bucketOverview(b.bucket, b.label)));
    totals = all.map((o) => ({ bucket: o.bucket, n: o.files.length, size: o.totalSize }));
    ov = all.find((o) => o.bucket === def.bucket)!;
    refs = await repo.referenceInfo(def.bucket);
  } catch (e) { root.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  // Filter periode (tanggal objek di server).
  const files = ov.files.filter((f) => {
    const d = (f.updatedAt ?? '').slice(0, 10);
    return (!saDari || d >= saDari) && (!saSampai || d <= saSampai);
  }).sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));

  const bar = BUCKETS.map((b) => {
    const t = totals.find((x) => x.bucket === b.bucket);
    return `<button class="btn ${saTab === b.bucket ? 'primary' : ''}" data-satab="${b.bucket}" style="padding:5px 11px;font-size:12.5px">${b.label} <span style="font-size:11px;opacity:.75">${t?.n ?? 0} · ${fmtBytes(t?.size ?? 0)}</span></button>`;
  }).join(' ');

  const rows = files.map((f) => {
    const r = refs.get(f.path);
    // Baris 1 = acuan yang bisa diklik (tiket / nomor dokumen beban), baris 2 = keterangan
    // "ini milik siapa": pelanggan & unit, siapa mengajukan & siapa memutus, dsb.
    const judul = r
      ? (r.ticketNo ? `<a class="tno-link" data-no="${esc(r.ticketNo)}">${esc(r.label)}</a>`
        : (r.expenseId ? `<button class="lnk-btn" data-beban="${r.expenseId}" title="Lihat rincian beban"><b>${esc(r.label)}</b></button>` : esc(r.label)))
      : '<span class="pill" style="background:var(--warnbg);color:var(--warn)">Orphan</span>';
    const taut = judul + (r?.detail ? `<div style="font-size:11px;color:var(--muted)">${esc(r.detail)}</div>` : '');
    return `<tr>
      <td>${f.referenced ? '' : `<input type="checkbox" class="sa_cek" data-p="${esc(f.path)}">`}</td>
      <td><input type="checkbox" class="sa_dl" data-p="${esc(f.path)}"></td>
      <td><button class="btn" style="padding:2px 7px" data-prev="${esc(f.path)}" title="Preview foto" aria-label="Preview foto">👁️</button></td>
      <td style="font-size:12px">${taut}${f.referenced ? ' <span title="Bukti tertaut record — dilindungi dari hapus">🔒</span>' : ''}</td>
      <td class="r">${fmtBytes(f.size)}</td>
      <td style="font-size:12px">${esc((f.updatedAt ?? '').slice(0, 10) || '—')}</td>
    </tr>`;
  }).join('') || `<tr><td colspan="6" class="empty">Tidak ada file${saDari || saSampai ? ' pada periode ini' : ''}.</td></tr>`;

  const pml = await bacaPemeliharaan();
  const isOwner = currentUser()?.role === 'Owner';
  // Panel pemeliharaan sengaja HANYA untuk Owner: menyalakannya menghentikan pekerjaan semua
  // orang, jadi ia bukan tombol yang boleh tersedia "sekadar ada".
  const panelPml = isOwner ? `
    <div class="panel" style="margin-bottom:12px;border-color:${pml.on ? 'var(--warn)' : 'var(--line)'};
      background:${pml.on ? 'var(--warnbg)' : '#fff'}">
      <div class="ttl" style="font-size:14px;margin-bottom:4px">🔧 Mode Pemeliharaan</div>
      <div class="scope-note">Menutup aplikasi untuk <b>semua peran kecuali Owner</b>, supaya tak ada transaksi
        masuk saat aplikasi sedang diperbarui. Nyalakan sebelum deploy, matikan sesudah dicek.
        <b>Catatan jujur:</b> ini penjaga di layar — permintaan yang telanjur berjalan tidak dihentikan,
        jadi untuk migrasi berbahaya tetap perlu penjagaan di sisi basis data.</div>
      ${pml.on ? `<div style="font-size:12.5px;color:var(--warn);font-weight:600;margin:6px 0">
        AKTIF sejak ${esc((pml.mulai ?? '').replace('T', ' ').slice(0, 16))}${pml.oleh ? ' oleh ' + esc(pml.oleh) : ''}</div>` : ''}
      <div class="row2">
        <div class="field"><label>Pesan untuk petugas</label><input id="pml_pesan" value="${esc(pml.pesan)}"></div>
        <div class="field"><label>Perkiraan selesai</label><input id="pml_kira" value="${esc(pml.perkiraan)}" placeholder="±15 menit"></div>
      </div>
      <button class="btn ${pml.on ? '' : 'primary'}" id="pml_toggle"
        style="${pml.on ? 'border-color:var(--ok);color:var(--ok)' : ''}">${pml.on ? '✅ Selesai — buka kembali aplikasi' : '🔧 Nyalakan Mode Pemeliharaan'}</button>
    </div>` : '';

  root.innerHTML = panelPml + `
    <div class="scope-note">Kelola foto tersimpan per jenis. <b>👁️ Preview</b> · centang kolom <b>⬇️</b> lalu "Unduh terpilih" (nama file otomatis: nomor tiket + nama pelanggan) · centang kolom <b>🗑️</b> (hanya tersedia utk <b>orphan</b>) lalu "Hapus terpilih". Foto 🔒 tertaut record = bukti hukum/hadir/akuntansi — dilindungi dari hapus.</div>
    <div style="display:flex;gap:6px;margin:10px 0;flex-wrap:wrap">${bar}</div>
    <div class="toolbar" style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin:6px 0">
      <input type="month" id="sa_bulan" title="Pilih bulan (mengisi rentang otomatis)" style="max-width:150px">
      <input type="date" id="sa_dari" value="${esc(saDari)}" title="Dari tanggal" style="max-width:140px">
      <input type="date" id="sa_sampai" value="${esc(saSampai)}" title="Sampai tanggal" style="max-width:140px">
      <button class="btn" id="sa_reset" style="padding:4px 9px" title="Bersihkan periode" aria-label="Bersihkan periode">↺</button>
      <span style="flex:1"></span>
      <button class="btn" id="sa_dlsel" style="padding:4px 10px">⬇️ Unduh terpilih</button>
      <button class="btn" id="sa_delsel" style="padding:4px 10px;border-color:var(--danger);color:var(--danger)">🗑️ Hapus terpilih (orphan)</button>
    </div>
    <div style="overflow-x:auto"><table class="data"><thead><tr>
      <th class="nosort" style="width:34px" title="pilih untuk HAPUS (hanya orphan)">🗑️<br><input type="checkbox" id="sa_cekall"></th>
      <th class="nosort" style="width:34px" title="pilih untuk UNDUH">⬇️<br><input type="checkbox" id="sa_dlall"></th>
      <th class="nosort">👁️</th><th>Tertaut ke</th><th class="r">Ukuran</th><th>Tanggal</th>
    </tr></thead><tbody>${rows}</tbody></table></div>
    <div class="scope-note" style="font-size:12px;margin-top:6px">${files.length} file tampil · total ${fmtBytes(files.reduce((a, f) => a + f.size, 0))}</div>
    ${saTab === 'absensi' ? `<div class="panel" style="margin-top:10px;padding:10px;border:1px solid var(--line);border-radius:8px">
      <b>🧹 Auto-hapus selfie</b> lebih tua dari
      <input id="sa_retensi" inputmode="numeric" value="${kebijakan().selfieRetensiHari}" style="width:70px;text-align:right;margin:0 4px"> hari
      <button class="btn" id="sa_retensisave" style="padding:3px 10px">💾 Simpan</button>
      <span style="font-size:12px;color:var(--muted)">(0 = mati · rekap kehadiran TETAP utuh, hanya fotonya yang dihapus · berjalan otomatis saat tab ini dibuka${selfieDibersihkan ? ` · <b style="color:var(--ok)">baru saja ${selfieDibersihkan} selfie dibersihkan</b>` : ''})</span>
    </div>` : ''}`;

  const refresh = (): void => { void renderStoragePanel(root); };
  root.querySelector('#sa_retensisave')?.addEventListener('click', async () => {
    const v = Math.max(0, +((root.querySelector('#sa_retensi') as HTMLInputElement).value || 0));
    try { await saveSetting('kebijakan', { ...kebijakan(), selfieRetensiHari: v }); toast(`💾 Auto-hapus selfie: ${v ? v + ' hari' : 'mati'}`); refresh(); }
    catch (e) { toast((e as Error).message); }
  });
  root.querySelectorAll<HTMLElement>('button[data-satab]').forEach((b) => b.addEventListener('click', () => { saTab = b.dataset['satab'] ?? 'kuasa'; refresh(); }));
  root.querySelector('#pml_toggle')?.addEventListener('click', async () => {
    const pesan = (root.querySelector('#pml_pesan') as HTMLInputElement).value;
    const kira = (root.querySelector('#pml_kira') as HTMLInputElement).value;
    if (!pml.on && !confirm('Nyalakan Mode Pemeliharaan? Semua peran selain Owner tak akan bisa memakai aplikasi.')) return;
    try {
      await setPemeliharaan(!pml.on, pesan, kira);
      await logAudit({ action: pml.on ? 'MAINTENANCE_OFF' : 'MAINTENANCE_ON', module: 'settings', reason: pesan });
      toast(pml.on ? '✅ Aplikasi dibuka kembali' : '🔧 Mode Pemeliharaan menyala');
      void renderStoragePanel(root);
    } catch (e) { toast((e as Error).message); }
  });

  (root.querySelector('#sa_bulan') as HTMLInputElement | null)?.addEventListener('change', (e) => {
    const v = (e.target as HTMLInputElement).value;                    // 'YYYY-MM'
    if (!v) return;
    const [y, m] = v.split('-').map(Number);
    saDari = `${v}-01`;
    saSampai = `${v}-${String(new Date(y!, m!, 0).getDate()).padStart(2, '0')}`; // hari terakhir bulan itu
    refresh();
  });
  (root.querySelector('#sa_dari') as HTMLInputElement | null)?.addEventListener('change', (e) => { saDari = (e.target as HTMLInputElement).value; refresh(); });
  (root.querySelector('#sa_sampai') as HTMLInputElement | null)?.addEventListener('change', (e) => { saSampai = (e.target as HTMLInputElement).value; refresh(); });
  root.querySelector('#sa_reset')?.addEventListener('click', () => { saDari = ''; saSampai = ''; refresh(); });
  // Hanya baris TERLIHAT (paginator memotong per halaman): "pilih semua" yang diam-diam
  // mencentang ratusan baris halaman lain = hapus/unduh massal file yang tak pernah terlihat.
  const terlihat = (kelas: string): HTMLInputElement[] =>
    [...root.querySelectorAll<HTMLInputElement>(kelas)].filter((c) => (c.closest('tr') as HTMLElement | null)?.style.display !== 'none');
  (root.querySelector('#sa_cekall') as HTMLInputElement | null)?.addEventListener('change', (e) => {
    const on = (e.target as HTMLInputElement).checked;
    terlihat('.sa_cek').forEach((c) => { c.checked = on; });
  });
  (root.querySelector('#sa_dlall') as HTMLInputElement | null)?.addEventListener('change', (e) => {
    const on = (e.target as HTMLInputElement).checked;
    terlihat('.sa_dl').forEach((c) => { c.checked = on; });
  });

  // 👁️ Preview — modal <img> ber-signed-URL, dibuat saat diklik (bukan thumbnail per-baris: hemat request).
  root.querySelectorAll<HTMLElement>('button[data-beban]').forEach((b) => b.addEventListener('click', () => void bebanRinci(+(b.dataset['beban'] ?? 0))));
  root.querySelectorAll<HTMLElement>('button[data-prev]').forEach((b) => b.addEventListener('click', async () => {
    const path = b.dataset['prev'] ?? '';
    try {
      const url = await signedUrl(def.bucket, path, 300);
      const r = refs.get(path);
      const bg = openModal(`<div class="modal-head"><h3>👁️ ${esc(r?.label ?? path)}</h3><button class="x">×</button></div>
        <div class="modal-body" style="text-align:center"><img src="${esc(url)}" style="max-width:100%;max-height:70vh;border-radius:8px" alt="${esc(r?.label ?? 'foto')}"></div>
        <div class="modal-foot"><button class="btn" id="pv_c">Tutup</button></div>`);
      bg.querySelector('#pv_c')?.addEventListener('click', () => bg.remove());
    } catch (e) { toast((e as Error).message); }
  }));

  // ⬇️ Unduh terpilih — nama file dari peta tautan; berurutan (puluhan file aman).
  // ponytail: tanpa ZIP (dependensi baru); tambah JSZip bila kelak rutin unduh ratusan file sekali jalan.
  root.querySelector('#sa_dlsel')?.addEventListener('click', async () => {
    const picked = terlihat('.sa_dl').filter((c) => c.checked).map((c) => c.dataset['p'] ?? '');
    if (!picked.length) { toast('Centang dulu di kolom ⬇️.'); return; }
    // Lewat gerbang export yang SAMA dengan CSV/XLS/PDF. Isinya foto KTP surat kuasa, selfie
    // absensi, dan bukti bayar — mengeluarkannya berpuluh berkas sekaligus jelas termasuk yang
    // ingin dikendalikan aturan export, dan sebelumnya jalur ini melewatinya begitu saja
    // (termasuk tak meninggalkan catatan siapa mengunduh apa).
    if (!(await passExportGuard(`${def.bucket}-${picked.length}file`))) return;
    let ok = 0;
    for (const path of picked) {
      try {
        const blob = await repo.downloadBlob(def.bucket, path);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = refs.get(path)?.dlName ?? path.split('/').pop() ?? 'foto.jpg';
        a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        ok++;
      } catch { /* lanjut file berikutnya; ringkasan di akhir */ }
    }
    toast(`⬇️ ${ok}/${picked.length} file diunduh`);
  });

  // 🗑️ Hapus terpilih — hanya orphan (re-cek referensi di repo saat eksekusi) + tercatat audit.
  root.querySelector('#sa_delsel')?.addEventListener('click', async () => {
    const picked = terlihat('.sa_cek').filter((c) => c.checked).map((c) => c.dataset['p'] ?? '');
    if (!picked.length) { toast('Centang dulu di kolom 🗑️ (hanya file orphan yang bisa dipilih).'); return; }
    if (!confirm(`Hapus ${picked.length} file orphan dari ${def.label}?\nPastikan sudah diunduh bila masih diperlukan.`)) return;
    try {
      const r = await repo.deleteOrphans(def.bucket, picked);
      void logAudit({ action: 'STORAGE_DELETE', module: 'backup', refId: `${def.bucket} · ${r.deleted} file`, meta: { paths: picked.slice(0, 50) } });
      toast(`🗑️ ${r.deleted} dihapus${r.protectedN ? ` · ${r.protectedN} DILINDUNGI (tertaut)` : ''}`);
      refresh();
    } catch (e) { toast((e as Error).message); }
  });
}


/** Rincian satu beban — dibuka dari bukti di menu Penyimpanan (nomor dokumen diklik). */
async function bebanRinci(id: number): Promise<void> {
  let x: Awaited<ReturnType<typeof finRepo.getExpense>>;
  try { x = await finRepo.getExpense(id); } catch (e) { toast((e as Error).message); return; }
  const baris = (k: string, v: string): string => `<div class="row"><span class="k">${esc(k)}</span><span>${esc(v)}</span></div>`;
  const bg = openModal(`<div class="modal-head"><h3>🧾 ${esc(x.no_dok || 'Beban #' + x.id)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      ${baris('Keterangan', x.nama)}
      ${baris('Kategori', x.kategori ?? '—')}
      ${baris('Jumlah', 'Rp ' + x.jumlah.toLocaleString('id-ID'))}
      ${baris('Tanggal', x.tgl_iso)}
      ${baris('Cabang', x.outlet ?? '—')}
      ${baris('Dicatat oleh', x.by_user ?? '—')}
      ${x.catatan ? baris('Catatan', x.catatan) : ''}
    </div>
    <div class="modal-foot">${x.bukti ? '<button class="btn" id="bb_lihat">📎 Lihat bukti</button>' : ''}<button class="btn primary" id="bb_tutup">Tutup</button></div>`);
  bg.querySelector('#bb_tutup')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#bb_lihat')?.addEventListener('click', async () => {
    try { window.open(await finRepo.buktiSignedUrl(x.bukti ?? ''), '_blank'); } catch (e) { toast((e as Error).message); }
  });
}
